import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

/**
 * How out of date this tab is.
 *
 * `optional` and `required` are deliberately different signals rather than
 * degrees of the same one. Measured across the last 12 months of releases, the
 * changes that actually break an already-loaded tab — a route removed or moved,
 * validation an old client would fail — are close to non-existent, while assets
 * rebuild on most minor releases. So an asset change alone is advisory, and
 * `required` is reserved for the cases where the tab is genuinely untrustworthy.
 */
export type BuildStaleness = 'none' | 'optional' | 'required';

export type RequiredReason = 'contract' | 'chunk-404' | 'server-rejected';

export const useBuildStore = defineStore('build', () => {
  /** Build this tab loaded with. Empty until seeded from the Inertia props. */
  const bootBuildId = ref('');
  const latestBuildId = ref('');

  /** Client contract this tab loaded with; compared against the server's. */
  const bootContract = ref(0);

  /** A newer build exists but this tab still works — offer a reload. */
  const updateAvailable = ref(false);

  /** This tab can no longer be trusted — insist on a reload. */
  const refreshRequired = ref(false);
  const requiredReason = ref<RequiredReason | null>(null);

  /** Epoch ms until which the optional prompt stays hidden after "Later". */
  const dismissedUntil = ref(0);

  /** False under `npm run dev`, where chunk hashes are meaningless. */
  const enabled = ref(false);

  const staleness = computed<BuildStaleness>(() => {
    if (refreshRequired.value) {
      return 'required';
    }

    if (enabled.value && updateAvailable.value) {
      return 'optional';
    }

    return 'none';
  });

  /**
   * Escalate to a mandatory reload. Safe to call repeatedly and from anywhere —
   * the first reason wins, so the UI doesn't flip its explanation around.
   */
  const requireRefresh = (reason: RequiredReason) => {
    if (refreshRequired.value) {
      return;
    }

    refreshRequired.value = true;
    requiredReason.value = reason;
  };

  return {
    bootBuildId,
    bootContract,
    dismissedUntil,
    enabled,
    latestBuildId,
    refreshRequired,
    requireRefresh,
    requiredReason,
    staleness,
    updateAvailable,
  };
});
